import {
  useEffect,
  useId,
  useRef,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from "react";
import {
  allowsDialogDismissal,
  classNames,
  isDialogBackdropInteraction,
  type DialogDismissReason,
} from "./component-model";
import { syncDialogOpenState } from "./dialog-model";
import { captureFocusReturn, focusTarget } from "./focus";

export interface DialogProps {
  readonly id?: string;
  readonly open: boolean;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
  readonly className?: string;
  readonly closeLabel?: string;
  readonly closeOnEscape?: boolean;
  readonly closeOnBackdrop?: boolean;
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly onRequestClose: (reason: DialogDismissReason) => void;
}

export function Dialog({
  id,
  open,
  title,
  description,
  children,
  actions,
  className,
  closeLabel = "Close dialog",
  closeOnEscape = true,
  closeOnBackdrop = false,
  initialFocusRef,
  onRequestClose,
}: DialogProps) {
  const generatedId = useId();
  const dialogId = id ?? `wh-dialog-${generatedId}`;
  const titleId = `${dialogId}-title`;
  const descriptionId = description ? `${dialogId}-description` : undefined;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const restoreFocusRef = useRef<(() => boolean) | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        restoreFocusRef.current = captureFocusReturn();
      }
      syncDialogOpenState(dialog, true);

      const frame = window.requestAnimationFrame(() => {
        const initialTarget =
          initialFocusRef?.current ??
          dialog.querySelector<HTMLElement>("[data-dialog-initial-focus]") ??
          dialog.querySelector<HTMLElement>(
            "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])",
          ) ??
          dialog;
        focusTarget(initialTarget, { scroll: false });
      });

      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    syncDialogOpenState(dialog, false);
    restoreFocusRef.current?.();
    restoreFocusRef.current = null;
  }, [initialFocusRef, open]);

  useEffect(
    () => () => {
      const dialog = dialogRef.current;
      if (dialog?.open) dialog.close();
      restoreFocusRef.current?.();
      restoreFocusRef.current = null;
    },
    [],
  );

  const requestClose = (reason: DialogDismissReason) => {
    if (
      allowsDialogDismissal(reason, {
        closeOnEscape,
        closeOnBackdrop,
      })
    ) {
      onRequestClose(reason);
    }
  };

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    requestClose("escape");
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (isDialogBackdropInteraction(event.target, event.currentTarget)) {
      requestClose("backdrop");
    }
  };

  const handleNativeClose = () => {
    if (open) {
      onRequestClose("native-close");
    }
  };

  return (
    <dialog
      ref={dialogRef}
      id={dialogId}
      className={classNames("wh-dialog", className)}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={handleCancel}
      onClose={handleNativeClose}
      onClick={handleBackdropClick}
    >
      <div className="wh-dialog__surface">
        <header className="wh-dialog__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <button
            type="button"
            className="wh-dialog__close"
            aria-label={closeLabel}
            onClick={() => requestClose("close-button")}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <div className="wh-dialog__body">{children}</div>
        {actions && <footer className="wh-dialog__footer">{actions}</footer>}
      </div>
    </dialog>
  );
}
